import { LoadingOverlayComponent } from '../shared/components/loading-overlay/loading-overlay.component';
import { GlobalStationSearchBarComponent } from '../shared/global-station-search-bar/global-station-search-bar.component';

import { AuthService } from '../auth/auth.service';
import { EppService } from '../epp/epp.service';
import { ThemeService } from '../shared/services/theme.service';
import {
  StationInfo,
  StationListItem,
  StationService,
} from '../shared/station.service';
import { EpsService } from './eps.service';

import { IsNilPipe } from '../pipes/is-nil.pipe';
import { DatexPipe } from '../shared/datex.pipe';

import { ResetInputMatAutocompleteDirective } from '../shared/directives/reset-input-mat-autocomplete.directive';

import { BreakpointObserver } from '@angular/cdk/layout';
import { Platform } from '@angular/cdk/platform';
import { CommonModule } from '@angular/common';
import {
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  Injector,
  OnDestroy,
  OnInit,
  signal,
  untracked,
  ViewChild,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormBuilder, ReactiveFormsModule } from '@angular/forms';
import {
  MatAutocompleteModule,
  MatAutocompleteSelectedEvent,
} from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectChange, MatSelectModule } from '@angular/material/select';
import { MatTooltip } from '@angular/material/tooltip';
import { ActivatedRoute, Router } from '@angular/router';

import { TranslateModule, TranslateService } from '@ngx-translate/core';

import { FaIconComponent } from '@fortawesome/angular-fontawesome';

import { isNil, isUndefined } from '../../utils/utils';
import {
  normalizeStationSearch,
  StationSearch,
} from '../shared/global-station-search-bar/models/station-search';
import Highcharts from 'highcharts';
import highchartsMore from 'highcharts/highcharts-more';
import HC_accessibility from 'highcharts/modules/accessibility';
import { HighchartsChartModule } from 'highcharts-angular';
import { round } from 'lodash';
import moment from 'moment/moment';
import { map, Observable, startWith, Subscription, timer } from 'rxjs';

export const MODULES = [
  CommonModule,
  ReactiveFormsModule,
  MatButtonModule,
  MatIconModule,
  MatFormFieldModule,
  MatSelectModule,
  MatInputModule,
  MatAutocompleteModule,
  MatProgressSpinnerModule,
  MatCardModule,
  HighchartsChartModule,
  TranslateModule,
];
export const COMPONENTS = [FaIconComponent, GlobalStationSearchBarComponent];
export const PIPES = [IsNilPipe];
export const DIRECTIVES = [ResetInputMatAutocompleteDirective];
type ComparisonStatistic = 'avg' | 'min' | 'max';
type ComparisonMode = 'ensemble' | 'difference';

@Component({
  providers: [EpsService],
  selector: 'app-eps',
  templateUrl: './eps.component.html',
  styleUrl: './eps.component.scss',
  imports: [
    ...MODULES,
    ...COMPONENTS,
    ...PIPES,
    ...DIRECTIVES,
    DatexPipe,
    MatTooltip,
    LoadingOverlayComponent,
  ],
})
export class EpsComponent implements OnInit, OnDestroy {
  private injector = inject(Injector);
  private fb = inject(FormBuilder);
  private platform = inject(Platform);
  private breakpointObserver = inject(BreakpointObserver);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private eppService = inject(EppService);
  private epsService = inject(EpsService);
  private stationService = inject(StationService);
  public translate = inject(TranslateService);
  private auth = inject(AuthService);
  private themeService = inject(ThemeService);

  @ViewChild('chartShell', { static: false })
  private chartShellRef?: ElementRef<HTMLElement>;

  chartFullscreen = signal(false);
  readonly isChartFullscreen = computed(() => this.chartFullscreen());

  private fullscreenChangeHandler = () => {
    const isFullscreen =
      document.fullscreenElement === this.chartShellRef?.nativeElement;
    this.chartFullscreen.set(isFullscreen);
  };

  async toggleChartFullscreen(): Promise<void> {
    const element = this.chartShellRef?.nativeElement;
    if (!element) {
      return;
    }

    if (document.fullscreenElement === element) {
      await document.exitFullscreen();
    } else {
      await element.requestFullscreen();
    }
  }

  language = signal<string>(this.auth.getLanguage());
  private languageSubscription: Subscription;

  panelForm = this.fb.group({
    stationListItem: this.fb.control<null | StationListItem>(null),
    modelRun: this.fb.control<null | string>(null),
    parameter: this.fb.control<null | string>(null),
    model: this.fb.control<null | string>(null),
  });
  showMobileFormFields = signal(false);
  stationList = signal<Array<StationListItem>>([]);
  selectedStationListItem = signal<StationListItem>(undefined);
  infosForStations: Array<StationInfo>;
  selectedModelRun = signal<string>(undefined);
  modelRuns = signal<Array<string>>([]);
  stationListFilteredOptions$: Observable<StationListItem[]>;
  epsData;
  subscriptions: Subscription[] = [];
  meteogramData;
  showImperialUnits = false;
  selectedParameter = signal<string>(undefined);
  models = [];
  allParameters = signal<string[]>([]);
  allModels = signal<string[]>([]);
  selectedModel = signal<string>(undefined);

  availableModelRuns = signal<string[]>([]);
  unavailableModelRuns = signal<string[]>([]);

  private readonly modelRunStepHours = 6;
  private readonly axisPaddingRatio = 0.08;
  private fixedYAxisBounds = signal<{ min: number; max: number } | null>(null);

  expectedNextModelRun = computed(() => {
    const availableRuns = [...this.availableModelRuns()];
    if (!availableRuns.length) {
      return null;
    }

    const newestAvailableRun = availableRuns.sort(
      (a, b) => moment.utc(b).valueOf() - moment.utc(a).valueOf()
    )[0];

    return moment.utc(newestAvailableRun).add(this.modelRunStepHours, 'hours');
  });

  nextUpcomingModelRun = computed(() => {
    return this.expectedNextModelRun();
  });

  nextUpcomingModelRunHasData = computed(() => {
    const expected = this.expectedNextModelRun();
    if (!expected) {
      return false;
    }

    return this.availableModelRuns().some(run =>
      moment.utc(run).isSame(expected, 'minute')
    );
  });

  showUpcomingModelRunHint = computed(() => {
    return !!this.nextUpcomingModelRun();
  });

  details = signal({});

  Highcharts = Highcharts;

  chartConstructor = 'chart';
  chartOptions = signal({});
  chartCallback = null;
  updateFlag = false;
  loadingData = false;
  chartReady = false;
  private chartLoadingTimer: ReturnType<typeof setTimeout> | null = null;
  private readonly chartLoadingDelayMs = 150;

  chartMode = signal<'single' | 'comparison'>('single');
  readonly isComparisonMode = computed(() => this.chartMode() === 'comparison');

  comparisonMode = signal<ComparisonMode>('ensemble');
  comparisonStatistics: ComparisonStatistic[] = ['avg', 'min', 'max'];
  comparisonStatistic = signal<ComparisonStatistic>('avg');
  referenceRun = signal<string | null>(null);

  highChartsData = signal([]);
  lineChartParameters = ['50', 'avg'];
  barChartParameters = ['avg'];
  scatterChartParameters = ['min', '10', '50', '90', 'max'];

  /*private readonly parameterColors: Record<string, string> = {
    avg: '#56B4E9',
    '10': '#0072B2',
    '50': '#004949',
    '90': '#E69F00',
    min: '#009E73',
    max: '#D55E00',
    minmax: '#999999',
    band: '#0d47a1',
    rrr: '#2e7d32',
    power: '#6a1b9a',
    td: '#00838f',
    sn: '#5d4037',
    rh: '#f57c00',
    ff: '#388e3c',
    ffx: '#00796b',
    ne: '#455a64',
  };

  private getParameterColor(parameter: string): string {
    return this.parameterColors[parameter] ?? '#d32f2f';
  }*/

  constructor() {
    effect(
      () => {
        this.themeService.theme();

        if (!this.hasChartData()) {
          return;
        }

        if (this.isComparisonMode()) {
          void this.setComparisonChart();
        } else {
          this.setChartData();
          this.setHighChartOptions();
        }
      },
      { injector: this.injector }
    );
  }

  private readonly lightThemeColors: Record<string, string> = {
    avg: '#0284c7', // Deep Ocean Blue
    '10': '#0369a1', // Dark Sky Blue
    '50': '#0f766e', // Deep Teal
    '90': '#b45309', // Dark Amber
    min: '#15803d', // Forest Green
    max: '#c2410c', // Dark Orange
    minmax: '#64748b', // Slate Grey
    band: '#1d4ed8', // Royal Blue
    rrr: '#166534', // Dark Grass Green
    power: '#701a75', // Dark Magenta
    td: '#0e7490', // Cyan Dark
    sn: '#7c2d12', // Rust Brown
    rh: '#c2410c', // Orange
    ff: '#15803d', // Green
    ffx: '#0f766e', // Teal
    ne: '#475569', // Slate
  };

  private readonly darkThemeColors: Record<string, string> = {
    avg: '#38bdf8', // Neon Sky Blue (leuchtet auf dunklem Glas)
    '10': '#60a5fa', // Soft Blue
    '50': '#2dd4bf', // Bright Teal
    '90': '#fbbf24', // Amber Glow
    min: '#4ade80', // Neon Green
    max: '#fb923c', // Bright Orange
    minmax: '#94a3b8', // Light Slate Grey
    band: '#3b82f6', // Bright Blue
    rrr: '#4ade80', // Soft Green
    power: '#e879f9', // Neon Orchid
    td: '#22d3ee', // Bright Cyan
    sn: '#b45309', // Light Wood Brown
    rh: '#fb923c', // Bright Orange
    ff: '#4ade80', // Mint Green
    ffx: '#2dd4bf', // Bright Teal
    ne: '#94a3b8', // Slate Grey
  };

  private getParameterColor(parameter: string): string {
    const isDarkMode = this.isDarkMode();
    const palette = isDarkMode ? this.darkThemeColors : this.lightThemeColors;
    return palette[parameter] ?? '#ef4444';
  }

  private isDarkMode(): boolean {
    return this.themeService.theme() === 'dark';
  }

  private isNumericValue(value: unknown): value is number {
    return typeof value === 'number' && Number.isFinite(value);
  }

  private calculatePaddedBounds(
    min: number,
    max: number
  ): { min: number; max: number } {
    if (!Number.isFinite(min) || !Number.isFinite(max)) {
      return null;
    }

    if (min === max) {
      const padding =
        min === 0 ? 1 : Math.abs(min) * this.axisPaddingRatio || 1;
      return {
        min: min - padding,
        max: max + padding,
      };
    }

    const range = max - min;
    const padding = range * this.axisPaddingRatio;

    return {
      min: min - padding,
      max: max + padding,
    };
  }

  private extractNumericValuesFromDataset(dataset: any): number[] {
    const weatherParameters = dataset?.weatherparameters?.[0];
    if (!weatherParameters) {
      return [];
    }

    if (
      this.selectedParameter() === 'rrr' ||
      this.selectedParameter() === 'power'
    ) {
      const values: number[] = [];
      for (const parameter of this.scatterChartParameters) {
        const value = weatherParameters?.[parameter];
        if (this.isNumericValue(value)) {
          values.push(value);
        }
      }
      return values;
    }

    const values: number[] = [];

    for (const parameter of this.lineChartParameters) {
      const value = weatherParameters?.[parameter];
      if (this.isNumericValue(value)) {
        values.push(value);
      }
    }

    for (const range of Object.values({
      minmax: ['min', 'max'],
      band: ['10', '90'],
    })) {
      const lowValue = weatherParameters?.[range[0]];
      const highValue = weatherParameters?.[range[1]];
      if (this.isNumericValue(lowValue)) {
        values.push(lowValue);
      }
      if (this.isNumericValue(highValue)) {
        values.push(highValue);
      }
    }

    return values;
  }

  private async updateFixedYAxisBoundsForAllRuns(): Promise<void> {
    const runs = [...(this.modelRuns() ?? [])];
    const stationId = this.selectedStationListItem()?.stationId;
    const model = this.selectedModel();
    const parameter = this.selectedParameter();

    if (!stationId || !model || !parameter || runs.length === 0) {
      this.fixedYAxisBounds.set(null);
      return;
    }

    const runDatasets = await Promise.all(
      runs.map(run =>
        this.epsService.getEpsData(model, stationId, parameter, run, true)
      )
    );

    const allValues: number[] = [];

    for (const runData of runDatasets) {
      if (!Array.isArray(runData)) {
        continue;
      }

      for (const dataset of runData) {
        allValues.push(...this.extractNumericValuesFromDataset(dataset));
      }
    }

    if (!allValues.length) {
      this.fixedYAxisBounds.set(null);
      return;
    }

    const rawMin = Math.min(...allValues);
    const rawMax = Math.max(...allValues);
    const bounds = this.calculatePaddedBounds(rawMin, rawMax);

    if (!bounds) {
      this.fixedYAxisBounds.set(null);
      return;
    }

    if (parameter === 'rrr') {
      bounds.min = Math.max(0, bounds.min);
    }

    this.fixedYAxisBounds.set(bounds);
  }

  private handset = toSignal(
    this.breakpointObserver
      .observe(['(max-width: 900px)'])
      .pipe(map(r => r.matches)),
    { initialValue: this.breakpointObserver.isMatched(['(max-width: 900px)']) }
  );

  readonly isMobilePlatform = computed(
    () => this.handset() || this.platform.ANDROID || this.platform.IOS
  );

  ngOnInit() {
    this.languageSubscription = this.translate.onLangChange.subscribe(event => {
      this.language.set(event.lang);
    });
    document.addEventListener('fullscreenchange', this.fullscreenChangeHandler);
    this.setupAutomaticParameterSelection();
    this.setSelectedModel();

    highchartsMore(Highcharts);
    HC_accessibility(Highcharts);
    this.extractProduct();
    this.getInfoForStations();
    this.listenForStationListFilterChanges();
  }

  ngOnDestroy(): void {
    this.languageSubscription?.unsubscribe();
    this.subscriptions.forEach(s => s.unsubscribe());
    document.removeEventListener(
      'fullscreenchange',
      this.fullscreenChangeHandler
    );
  }

  get stationListItem() {
    return this.panelForm.get('stationListItem');
  }

  hasBetweenOneAndSixStations = computed(() => {
    const stationList = this.stationList();
    return stationList?.length > 1 && stationList?.length < 6;
  });

  hasMoreThanFiveStations = computed(() => {
    return this.stationList()?.length > 5;
  });

  private get epsRunsExists() {
    return this.epsService.epsRunsExists(
      this.selectedStationListItem().stationId
    );
  }

  validModels = computed<string[]>(() => {
    const models = [];
    const selectedStationListItem = this.selectedStationListItem();
    const details = this.details();
    const allModels = this.allModels();

    if (
      !selectedStationListItem ||
      !details.hasOwnProperty(selectedStationListItem.stationId)
    ) {
      return models;
    }
    for (const detail of details[selectedStationListItem.stationId]) {
      if (detail.modelDetails) {
        for (const modelDetail of detail.modelDetails) {
          if (
            modelDetail.model !== 'gfs' &&
            modelDetail.model !== 'icon' &&
            models.indexOf(modelDetail.model) < 0 &&
            allModels.indexOf(modelDetail.model) >= 0
          ) {
            models.push(modelDetail.model);
          }
        }
      }
    }
    return models;
  });

  private setSelectedModel() {
    effect(
      () => {
        const models = [];
        const selectedStationListItem = this.selectedStationListItem();
        const details = this.details();
        const allModels = this.allModels();
        const selectedModel = this.selectedModel();
        if (details.hasOwnProperty(selectedStationListItem.stationId)) {
          for (const detail of details[selectedStationListItem.stationId]) {
            if (detail.modelDetails) {
              for (const modelDetail of detail.modelDetails) {
                if (
                  modelDetail.model !== 'gfs' &&
                  modelDetail.model !== 'icon' &&
                  models.indexOf(modelDetail.model) < 0 &&
                  allModels.indexOf(modelDetail.model) >= 0
                ) {
                  models.push(modelDetail.model);
                  if (!selectedModel || selectedModel === '') {
                    this.setModel(modelDetail.model);
                  }
                }
              }
            }
          }
        }
      },
      { injector: this.injector }
    );
  }

  private setupAutomaticParameterSelection() {
    effect(
      () => {
        // Wir holen uns die aktuell gültigen Parameter für die gewählte Station/Model
        const currentValidParams = this.validParameters();
        const currentSelection = this.selectedParameter();

        // WICHTIG: untracked verhindert, dass das Setzen des Parameters
        // den Effekt im Kreis triggert (Endlosschleife)
        untracked(() => {
          if (currentValidParams.length > 0) {
            // Falls noch nichts gewählt ist ODER die aktuelle Wahl nicht mehr gültig ist
            if (
              !currentSelection ||
              !currentValidParams.includes(currentSelection)
            ) {
              const defaultParam = currentValidParams[0];
              this.setParameter(defaultParam);
            }
          }
        });
      },
      { injector: this.injector }
    );
  }

  validParameters = computed<string[]>(() => {
    const parameters = [];
    const selectedStationListItem = this.selectedStationListItem();
    const selectedModel = this.selectedModel();
    const details = this.details();
    const allParameters = this.allParameters();

    if (
      !selectedModel ||
      !selectedStationListItem ||
      !details.hasOwnProperty(selectedStationListItem.stationId)
    ) {
      return parameters;
    }

    const stationDetails = details[selectedStationListItem.stationId];
    for (const detail of stationDetails) {
      if (detail.modelDetails) {
        const modelDetail = detail.modelDetails.find(
          m => m.model === selectedModel
        );
        if (modelDetail) {
          for (const parameter of modelDetail.parameters) {
            if (
              parameters.indexOf(parameter) < 0 &&
              allParameters.indexOf(parameter) >= 0
            ) {
              parameters.push(parameter);
            }
          }
        }
      }
    }
    return parameters;
  });

  private setSelectedParameter() {
    effect(
      () => {
        const parameters = [];
        const selectedStationListItem = this.selectedStationListItem();
        const selectedModel = this.selectedModel();
        const details = this.details();
        const allParameters = this.allParameters();

        untracked(() => {
          for (const detail of details[selectedStationListItem.stationId]) {
            if (detail.modelDetails) {
              for (const modelDetail of detail.modelDetails) {
                if (modelDetail.model === selectedModel) {
                  for (const parameter of modelDetail.parameters) {
                    if (
                      parameters.indexOf(parameter) < 0 &&
                      allParameters.indexOf(parameter) >= 0
                    ) {
                      this.setParameter(parameter);
                      parameters.push(parameter);
                    }
                  }
                }
              }
            }
          }
        });
      },
      { injector: this.injector }
    );
  }

  public readonly calculatedParameters = computed(() => {
    const selectedStation = this.selectedStationListItem();
    const model = this.selectedModel();
    const details = this.details();
    const allParams = this.allParameters();

    if (!selectedStation || !details[selectedStation.stationId]) {
      return [];
    }

    const results: any[] = [];
    const stationDetails = details[selectedStation.stationId];

    for (const detail of stationDetails) {
      if (detail.modelDetails) {
        const match = detail.modelDetails.find(m => m.model === model);
        if (match) {
          for (const p of match.parameters) {
            if (!results.includes(p) && allParams.includes(p)) {
              results.push(p);
            }
          }
        }
      }
    }
    return results;
  });

  private async getInfoForStations() {
    this.infosForStations = await Promise.all(
      this.getStationsInfo(this.stationList())
    );

    this.stationList.set([
      ...this.addElevationInfoToStationList(
        this.stationList(),
        this.infosForStations
      ),
    ]);

    this.initStations();
  }

  private extractProduct() {
    const details: Record<number, any[]> = {};
    const stationList: StationListItem[] = [];

    const modelSet = new Set(this.allModels());
    const parameterSet = new Set(this.allParameters());

    let selectedStationListItem: StationListItem | undefined;
    let selectedModel: string | undefined;

    for (const product of this.auth.getCustomerDocument()['ensembleProducts']) {
      if (!this.auth.isProductActive('ensembleProducts', product.id)) {
        continue;
      }

      for (const epsDetail of product.ensembleDetails.sort((a, b) =>
        a.overwriteStationName > b.overwriteStationName ? 1 : -1
      )) {
        const station: StationListItem = {
          stationId: Number(epsDetail.stationId),
          overwriteStationName: epsDetail.overwriteStationName,
        };
        stationList.push(station);

        if (!details[station.stationId]) {
          details[station.stationId] = [];
        }
        details[station.stationId].push(epsDetail);

        if (isNil(selectedStationListItem)) {
          selectedStationListItem = { ...station };
        }

        for (const modelDetail of epsDetail.modelDetails) {
          modelSet.add(modelDetail.model);

          if (isNil(selectedModel)) {
            selectedModel = modelDetail.model;
          }

          for (const parameter of modelDetail.parameters) {
            parameterSet.add(parameter);
          }
        }
      }
    }

    this.stationList.set(stationList);
    this.details.set(details);
    this.allModels.set(Array.from(modelSet));
    this.allParameters.set(Array.from(parameterSet));

    if (selectedStationListItem) {
      this.selectedStationListItem.set(selectedStationListItem);
    }

    if (selectedModel) {
      this.setModel(selectedModel);
    }

    this.selectedParameter.set(this.validParameters()[0]);
    this.setParameter(this.selectedParameter());
  }

  listenForStationListFilterChanges() {
    this.stationListFilteredOptions$ = this.stationListItem.valueChanges.pipe(
      startWith(''),
      map(station => {
        const displayingStationName =
          typeof station === 'string'
            ? station
            : this.displayStationName(station);
        return displayingStationName
          ? this.filterStationsByOverwriteStationName(displayingStationName)
          : this.stationList().slice();
      })
    );
  }

  filterStationsByOverwriteStationName(
    displayingStationName: string
  ): StationListItem[] {
    const filterValue = displayingStationName.toLowerCase();

    return this.stationList().filter(station =>
      this.displayStationName(station).toLowerCase().includes(filterValue)
    );
  }

  displayStationName(station: StationListItem): string {
    const stationElevation = station?.elevation;
    const overwriteStationName = station?.overwriteStationName;

    let result = '';
    if (!isNil(overwriteStationName)) result = overwriteStationName;
    if (!isNil(stationElevation)) result += ` | ${stationElevation} m`;
    return result;
  }

  getStationList(): void {
    this.stationList.set([
      ...this.eppService
        .getEppStations()
        .sort((a, b) =>
          a.overwriteStationName > b.overwriteStationName ? 1 : -1
        ),
    ]);
  }

  getStationsInfo(
    stationList: Array<StationListItem>
  ): Array<Promise<StationInfo>> {
    // requests additional Information of station (e.g. elevation)
    const allPromisesForStationData = [];

    stationList.forEach(stationListItem => {
      const stationInformation$ = this.stationService.getStationInfo(
        stationListItem.stationId + ''
      );

      allPromisesForStationData.push(
        new Promise((resolve, reject) => {
          stationInformation$.subscribe(
            data2 => {
              resolve(data2);
            },
            error => {
              console.log('ERROR:', error);
              // needed to be able to continue even when there was an error,
              // null will be handled when trying to combine elevation & station in addElevationInfoToStationList()
              resolve(null);
            }
          );
        })
      );
    });
    return allPromisesForStationData;
  }

  addElevationInfoToStationList(
    stationList: Array<StationListItem>,
    infosForStations: Array<StationInfo>
  ): Array<StationListItem> {
    if (
      !Array.isArray(stationList) ||
      !stationList.length ||
      !Array.isArray(infosForStations) ||
      !infosForStations.length
    ) {
      return stationList;
    }

    return stationList.map(station => {
      const stationInformation = infosForStations.find(stationInfo => {
        // null means there was an error with receiving the stationInformation data
        // in this case: dont try to find matching station & stationInfo
        if (stationInfo !== null) {
          return station.stationId == stationInfo.stationId;
        }
      });

      if (stationInformation) {
        station['elevation'] = stationInformation.elevation;
      }

      return station;
    });
  }

  autoRefreshEpsData() {
    const autoRefreshEpsDataSubscription = timer(0, 300000).subscribe(() => {
      this.getEpsData(false, false);
    });
    this.subscriptions.push(autoRefreshEpsDataSubscription);
  }

  autoRefreshEpsRuns() {
    const autoRefreshEpsRunsSubscription = timer(0, 300000).subscribe(() => {
      this.getEpsRuns(false, false);
    });
    this.subscriptions.push(autoRefreshEpsRunsSubscription);
  }

  initStations(): void {
    if (this.stationList().length > 0) {
      this.route.params.subscribe(params => {
        if (params && params.hasOwnProperty('station')) {
          const stationId = params['station'];
          const currentStation = this.findStationById(stationId);
          if (!isUndefined(currentStation)) {
            this.setStationListItem(currentStation);
            if (!this.epsRunsExists) {
              this.autoRefreshEpsRuns();
            }
            if (!isNil(this.selectedModelRun())) {
              this.autoRefreshEpsData();
            } else {
              //this.setMeteogramData();
            }
          }
        } else {
          const station = isNil(this.selectedStationListItem())
            ? this.stationList()[0]
            : this.selectedStationListItem();
          this.router.navigate(['/eps', station.stationId]);
        }
      });
    }
  }

  findStationById(stationId) {
    return this.stationList()?.find(s => s.stationId == stationId);
  }

  setStationListItem(currentStation: StationListItem) {
    this.panelForm.patchValue({
      stationListItem: currentStation,
    });
    this.selectedStationListItem.set({ ...currentStation });
  }

  setModelRun(currentModelRun: string) {
    this.panelForm.patchValue({
      modelRun: currentModelRun,
    });
    this.selectedModelRun.set(currentModelRun);
  }

  setParameter(currentParameter: string) {
    this.panelForm.patchValue({
      parameter: currentParameter,
    });
    this.selectedParameter.set(currentParameter);
  }

  setModel(currentModel: string) {
    this.panelForm.patchValue({
      model: currentModel,
    });
    this.selectedModel.set(currentModel);
  }

  hasChartData = computed(() => {
    const data = this.highChartsData();
    return Array.isArray(data) && data.length > 0;
  });

  showChartLoading = computed(() => {
    return this.loadingData || (!this.chartReady && !this.hasChartData());
  });

  showChartEmptyState = computed(() => {
    return !this.loadingData && this.chartReady && !this.hasChartData();
  });

  private startChartLoading(): void {
    this.chartReady = false;
    this.loadingData = true;

    if (this.chartLoadingTimer) {
      clearTimeout(this.chartLoadingTimer);
      this.chartLoadingTimer = null;
    }
  }

  onGlobalStationSelect(station: StationSearch): void {
    const normalized = normalizeStationSearch(station);
    if (!normalized) {
      return;
    }

    this.startChartLoading();

    if (!this.stationList().some(s => s.stationId == normalized.id)) {
      this.stationList.update(stationList => [
        ...stationList,
        {
          stationId: normalized.id,
          overwriteStationName:
            normalized.overwriteStationName ?? normalized.name,
          elevation: normalized.elevation ?? undefined,
        },
      ]);
    }

    this.setStationListItem({
      stationId: normalized.id,
      overwriteStationName: normalized.overwriteStationName ?? normalized.name,
      elevation: normalized.elevation ?? undefined,
    });

    this.chartOptions.set({});
    this.highChartsData.set([]);
    this.fixedYAxisBounds.set(null);

    this.router.navigate(['/eps', normalized.id]).then(() => {
      const availableModels = this.validModels();
      if (!this.selectedModel() && availableModels.length > 0) {
        this.setModel(availableModels[0]);
      }

      const validParams = this.validParameters();
      if (!this.selectedParameter() && validParams.length > 0) {
        this.setParameter(validParams[0]);
      }

      this.getEpsRuns(true);
    });
  }

  async getEpsRuns(reload = false, showSpinner = true) {
    this.startChartLoading();
    this.meteogramData = null;

    try {
      const data = await this.epsService.getRunsOfStation(
        this.selectedModel(),
        this.selectedStationListItem().stationId,
        reload
      );

      const available: string[] = [];
      const unavailable: string[] = [];

      const sortedRuns = [...data].sort(
        (a, b) => moment.utc(b).valueOf() - moment.utc(a).valueOf()
      );

      for (const modelRun of sortedRuns) {
        const runData = await this.epsService.getEpsData(
          this.selectedModel(),
          this.selectedStationListItem().stationId,
          this.selectedParameter(),
          modelRun,
          true
        );

        if (Array.isArray(runData) && runData.length > 0) {
          available.push(modelRun);
        } else {
          unavailable.push(modelRun);
        }
      }

      const availableSorted = available.sort(
        (a, b) => moment.utc(b).valueOf() - moment.utc(a).valueOf()
      );
      const unavailableSorted = unavailable.sort(
        (a, b) => moment.utc(b).valueOf() - moment.utc(a).valueOf()
      );

      this.availableModelRuns.set(availableSorted);
      this.unavailableModelRuns.set(unavailableSorted);
      this.modelRuns.set([...availableSorted, ...unavailableSorted]);

      await this.updateFixedYAxisBoundsForAllRuns();

      if (
        isNil(this.selectedModelRun()) ||
        !this.availableModelRuns().includes(this.selectedModelRun())
      ) {
        this.setModelRun(this.availableModelRuns()[0]);
      }

      if (this.selectedModelRun()) {
        await this.getEpsData(false, true);
      } else {
        this.markChartAsIdle();
      }
    } catch (error) {
      console.error('getEpsRuns failed:', error);
      this.markChartAsIdle();
    }
  }

  async getEpsData(reload = false, showSpinner = true) {
    this.startChartLoading();
    this.meteogramData = null;

    try {
      const data = await this.epsService.getEpsData(
        this.selectedModel(),
        this.selectedStationListItem().stationId,
        this.selectedParameter(),
        this.selectedModelRun(),
        reload
      );

      this.epsData = data;

      if (this.isComparisonMode()) {
        await this.setComparisonChart();
      } else {
        this.highChartsData.set([]);
        this.setChartData();
        this.setHighChartOptions();
        this.chartReady = true;
      }
    } catch (error) {
      console.error('getEpsData failed:', error);
      this.highChartsData.set([]);
      this.chartReady = false;
    } finally {
      this.stopChartLoading();
    }
  }

  onStationChange(event: MatSelectChange | MatAutocompleteSelectedEvent): void {
    let stationId;
    if (event instanceof MatSelectChange) {
      stationId = event.source.value['stationId'];
      this.selectedStationListItem.set({ ...event.source.value });
    } else {
      stationId = event.option.value['stationId'];
      this.selectedStationListItem.set({ ...event.option.value });
    }

    this.resetToSingleMode();
    this.chartOptions.set({});
    this.highChartsData.set([]);
    this.fixedYAxisBounds.set(null);
    this.loadingData = true;

    setTimeout(() => {
      if (!this.validParameters().includes(this.selectedParameter())) {
        this.setParameter(this.validParameters()[0]);
      }
      this.router.navigate(['/eps', stationId]).then(() => {
        this.getEpsRuns(true);
      });
    });
  }

  onModelRunChange(event: MatSelectChange): void {
    this.resetToSingleMode();
    this.selectedModelRun.set(event.source.value);
    setTimeout(() => {
      this.getEpsData();
    });
  }

  onModelChange(event: MatSelectChange): void {
    this.resetToSingleMode();
    this.selectedModel.set(event.source.value);
    this.modelRuns.set([]);
    this.fixedYAxisBounds.set(null);

    setTimeout(() => {
      if (!this.validParameters().includes(this.selectedParameter())) {
        this.setParameter(this.validParameters()[0]);
      }
      this.getEpsRuns();
    });
  }

  onParameterChange(event: MatSelectChange): void {
    this.resetToSingleMode();
    this.selectedParameter.set(event.value);
    this.chartOptions.set({});
    this.fixedYAxisBounds.set(null);

    setTimeout(() => {
      this.highChartsData.set([]);
      void this.ensureModelRunWithDataForSelectedParameter();
    });
  }

  selectLatestModelRun(): void {
    const modelRuns = this.modelRuns() ?? [];
    this.startChartLoading();

    const tryNext = (index: number) => {
      if (index >= modelRuns.length) {
        this.markChartAsIdle();
        return;
      }

      const candidate = modelRuns[index];
      this.setModelRun(candidate);

      this.epsService
        .getEpsData(
          this.selectedModel(),
          this.selectedStationListItem().stationId,
          this.selectedParameter(),
          candidate,
          true
        )
        .then(data => {
          if (Array.isArray(data) && data.length > 0) {
            this.epsData = data;
            this.setChartData();
            this.setHighChartOptions();
            this.chartReady = true;
            this.stopChartLoading();
          } else {
            tryNext(index + 1);
          }
        })
        .catch(() => {
          tryNext(index + 1);
        });
    };

    tryNext(0);
  }

  private async ensureModelRunWithDataForSelectedParameter(): Promise<void> {
    const runs = [...this.modelRuns()].sort(
      (a, b) => moment.utc(b).valueOf() - moment.utc(a).valueOf()
    );

    for (const run of runs) {
      const data = await this.epsService.getEpsData(
        this.selectedModel(),
        this.selectedStationListItem().stationId,
        this.selectedParameter(),
        run,
        true
      );

      if (Array.isArray(data) && data.length > 0) {
        if (this.selectedModelRun() !== run) {
          this.setModelRun(run);
        }
        this.epsData = data;
        this.setChartData();
        await this.updateFixedYAxisBoundsForAllRuns();
        this.setHighChartOptions();
        return;
      }
    }
  }

  setHighChartOptions(): void {
    const isDarkMode = this.isDarkMode();

    // Dynamische Theme-Stile für die Chart-Grundstruktur
    const textColor = isDarkMode ? '#e5e7eb' : '#2c3e50';
    const mutedTextColor = isDarkMode
      ? 'rgba(229, 231, 235, 0.6)'
      : 'rgba(44, 62, 80, 0.6)';
    const gridLineColor = isDarkMode
      ? 'rgba(255, 255, 255, 0.12)'
      : 'rgba(0, 0, 0, 0.08)';

    const stationName =
      this.selectedStationListItem()?.overwriteStationName ?? '';
    const unit = this.translate.instant(
      'PRODUCT.EPS.UNITS.' + this.selectedParameter()
    );
    const parameterName = this.translate.instant(
      'PRODUCT.EPS.PARAMETERS.' + this.selectedParameter()
    );

    Highcharts.setOptions({
      lang: {
        shortWeekdays: this.translate.instant('CHART.shortWeekdays'),
        printChart: this.translate.instant('CHART.printChart'),
        downloadJPEG: this.translate.instant('CHART.downloadJPEG'),
        downloadPNG: this.translate.instant('CHART.downloadPNG'),
        downloadPDF: this.translate.instant('CHART.downloadPDF'),
        downloadSVG: this.translate.instant('CHART.downloadSVG'),
        viewFullscreen: this.translate.instant('CHART.viewFullscreen'),
      },
      time: {
        useUTC: true,
      },
    });

    this.chartOptions.set({
      chart: {
        zoomType: 'xy',
        marginLeft: 75,
        marginRight: 90,
        backgroundColor: 'transparent', // Wichtig: Lässt deine Glassmorphism Card durchscheinen
        style: {
          fontFamily: 'Roboto, "Helvetica Neue", sans-serif',
        },
      },
      title: {
        text:
          this.translate.instant('PRODUCT.EPS.TITLE') +
          ' ' +
          stationName +
          ' - ' +
          parameterName,
        align: 'left',
        style: { color: textColor },
      },
      xAxis: [
        {
          type: 'datetime',
          units: [['day', [1, 2]]],
          labels: {
            format: '{value:%a %d.%m.}', // bzw. format: '{value:%d.%m. %H:%M}' im Comparison-Chart
            style: {
              color: isDarkMode ? '#f3f4f6' : '#4a5568',
              fontSize: '11px',
              fontWeight: isDarkMode ? '500' : '400',
            },
          },
          lineColor: gridLineColor,
          tickColor: gridLineColor,
          crosshair: {
            color: gridLineColor,
            dashStyle: 'Dash',
          },
        },
      ],
      legend: {
        enabled: true,
        reversed: false,
        itemStyle: { color: textColor },
        itemHoverStyle: { color: isDarkMode ? '#ffffff' : '#000000' },
        itemHiddenStyle: { color: mutedTextColor },
      },
      yAxis: [
        {
          labels: {
            format: '{value} ' + unit,
            reserveSpace: true,
            style: {
              color: isDarkMode ? '#f3f4f6' : '#4a5568',
              fontSize: '11px',
              fontWeight: isDarkMode ? '500' : '400',
            },
          },
          title: {
            enabled: true,
            text: parameterName,
            style: {
              // Auch der Achsen-Titel (z.B. "Temperatur") bekommt ein klares, helles Finish
              color: isDarkMode ? '#ffffff' : '#2c3e50',
              fontWeight: '600',
            },
          },
          softMin: this.selectedParameter() === 'rrr' ? 0 : null,
          min: this.fixedYAxisBounds()?.min ?? undefined,
          max: this.fixedYAxisBounds()?.max ?? undefined,

          gridLineWidth: 1,
          gridLineColor: gridLineColor, // Flexibles Gitter statt hartem #808080
          plotLines: [
            {
              value: 0,
              color: isDarkMode ? '#f87171' : '#e27878', // Sichtbares, dezent angepasstes Null-Linien Rot
              width: 2,
              zIndex: 3,
            },
          ],
          startOnTick: false,
          endOnTick: false,
        },
      ],
      credits: {
        enabled: true,
        text: 'Wettermanufaktur GmbH',
        href: 'https://www.wettermanufaktur.de',
        style: { color: mutedTextColor },
      },
      tooltip: {
        shared: true,
        useHTML: true,
        headerFormat:
          '<small style="color:' +
          mutedTextColor +
          '">{point.x:%a, %d.%m. %H:%M}</small><br>',
        backgroundColor: isDarkMode
          ? 'rgba(15, 23, 42, 0.95)'
          : 'rgba(255, 255, 255, 0.95)',
        borderColor: gridLineColor,
        borderRadius: 8,
        shadow: true,
        style: { color: textColor },
      },
      series: this.highChartsData(),
      exporting: {
        enabled: true,
        buttons: {
          contextButton: {
            menuItems: [
              'downloadPNG',
              'downloadJPEG',
              'downloadPDF',
              'downloadSVG',
            ],
            enabled: true,
            // Export-Button-Styling an das Glassmorphic-Theme binden
            theme: {
              fill: 'transparent',
              states: {
                hover: {
                  fill: gridLineColor,
                },
              },
            },
          },
        },
      },
    });
  }

  getScatterChartData(): Array<any> {
    const scatterData = [];
    const isDarkMode = this.isDarkMode();

    for (const parameter of this.scatterChartParameters) {
      const dataForParameter = [];
      const unit = this.translate.instant(
        'PRODUCT.EPS.UNITS.' + this.selectedParameter()
      );
      const currentColor = this.getParameterColor(parameter);

      for (const dataset of this.epsData) {
        const dtg = dataset['dtg'];
        const parameterValue = dataset?.['weatherparameters']?.[0]?.[parameter];
        if (!isNil(parameterValue)) {
          dataForParameter.push({
            x: Date.parse(dtg),
            y: parameterValue,
          });
        }
      }

      if (dataForParameter.length > 0) {
        scatterData.push({
          name: this.translate.instant('PRODUCT.EPS.PARAMETERS.' + parameter),
          type: 'spline',
          data: dataForParameter,
          tooltip: {
            valueDecimals:
              this.selectedParameter() === 'fff' ||
              this.selectedParameter() === 'power'
                ? 0
                : 1,
            valueSuffix: ' ' + unit,
          },
          color: currentColor,
          lineWidth: 2.5, // Etwas dicker für bessere Abhebung auf Glas
          states: {
            hover: {
              lineWidthPlus: 1, // Sanfte Linienverdickung bei Mouseover
            },
          },
          marker: {
            enabled: true,
            lineColor: currentColor,
            fillColor: isDarkMode ? '#0f172a' : '#ffffff', // Macht Marker-Punkte innen hohl/transparent passend zur Card
            symbol: 'circle',
            radius: 3, // Minimal größer für bessere Touch-Targets
            states: {
              hover: {
                radius: 5,
                fillColor: currentColor, // Füllt den Punkt beim Hovern voll aus
              },
            },
          },
        });
      }
    }

    return scatterData;
  }

  getBarChartData(): Array<any> {
    const barData = [];

    for (const parameter of this.barChartParameters) {
      const dataForParameter = [];
      const unit = this.translate.instant(
        'PRODUCT.EPS.UNITS.' + this.selectedParameter()
      );
      const currentColor = this.getParameterColor(parameter);

      for (const dataset of this.epsData) {
        const dtg = dataset['dtg'];
        const parameterValue = dataset?.['weatherparameters']?.[0]?.[parameter];
        if (!isNil(parameterValue)) {
          dataForParameter.push({
            x: Date.parse(dtg),
            y: parameterValue,
          });
        }
      }

      if (dataForParameter.length > 0) {
        barData.push({
          name: this.translate.instant('PRODUCT.EPS.PARAMETERS.' + parameter),
          type: 'column',
          data: dataForParameter,
          tooltip: {
            valueDecimals:
              this.selectedParameter() === 'fff' ||
              this.selectedParameter() === 'power'
                ? 0
                : 1,
            valueSuffix: ' ' + unit,
          },
          color: currentColor,
          borderWidth: 0, // Entfernt unschöne Standard-Rahmenkanten der Balken
          pointPadding: -0.5,
          marker: {
            enabled: false,
          },
        });
      }
    }

    return barData;
  }

  /*setHighChartOptions(): void {
    const stationName =
      this.selectedStationListItem()?.overwriteStationName ?? '';
    const unit = this.translate.instant(
      'PRODUCT.EPS.UNITS.' + this.selectedParameter()
    );
    const parameterName = this.translate.instant(
      'PRODUCT.EPS.PARAMETERS.' + this.selectedParameter()
    );

    Highcharts.setOptions({
      lang: {
        shortWeekdays: this.translate.instant('CHART.shortWeekdays'),
        printChart: this.translate.instant('CHART.printChart'),
        downloadJPEG: this.translate.instant('CHART.downloadJPEG'),
        downloadPNG: this.translate.instant('CHART.downloadPNG'),
        downloadPDF: this.translate.instant('CHART.downloadPDF'),
        downloadSVG: this.translate.instant('CHART.downloadSVG'),
        viewFullscreen: this.translate.instant('CHART.viewFullscreen'),
      },
      time: {
        useUTC: true,
      },
    });

    this.chartOptions.set({
      chart: {
        zoomType: 'xy',
        marginLeft: 75,
        marginRight: 90,
      },
      title: {
        text:
          this.translate.instant('PRODUCT.EPS.TITLE') +
          ' ' +
          stationName +
          ' - ' +
          parameterName,
        align: 'left',
      },
      xAxis: [
        {
          type: 'datetime',
          units: [['day', [1, 2]]],
          labels: {
            format: '{value:%a %d.%m.}',
          },
          crosshair: true,
        },
      ],
      legend: {
        enabled: true,
        reversed: false,
      },
      yAxis: [
        {
          labels: {
            format: '{value} ' + unit,
            reserveSpace: true,
          },
          title: {
            enabled: true,
            text: parameterName,
          },
          softMin: this.selectedParameter() === 'rrr' ? 0 : null,
          min: this.fixedYAxisBounds()?.min ?? undefined,
          max: this.fixedYAxisBounds()?.max ?? undefined,

          gridLineWidth: 1,
          gridLineColor: '#808080',
          plotLines: [
            {
              value: 0,
              color: '#e27878',
              width: 2,
              zIndex: 3,
            },
          ],
          startOnTick: false,
          endOnTick: false,
        },
      ],
      credits: {
        enabled: true,
        text: 'Wettermanufaktur GmbH',
        href: 'https://www.wettermanufaktur.de',
      },
      tooltip: {
        shared: true,
        useHTML: true,
        headerFormat: '<small>{point.x:%a, %d.%m. %H:%M}</small><br>',
      },
      series: this.highChartsData(),
      exporting: {
        enabled: true,
        buttons: {
          contextButton: {
            menuItems: [
              'downloadPNG',
              'downloadJPEG',
              'downloadPDF',
              'downloadSVG',
            ],
            enabled: true,
          },
        },
      },
    });
  }

  getScatterChartData(): Array<any> {
    const scatterData = [];

    for (const parameter of this.scatterChartParameters) {
      const dataForParameter = [];
      const unit = this.translate.instant(
        'PRODUCT.EPS.UNITS.' + this.selectedParameter()
      );

      for (const dataset of this.epsData) {
        const dtg = dataset['dtg'];
        const parameterValue = dataset?.['weatherparameters']?.[0]?.[parameter];
        if (!isNil(parameterValue)) {
          dataForParameter.push({
            x: Date.parse(dtg),
            y: parameterValue,
          });
        }
      }

      if (dataForParameter.length > 0) {
        scatterData.push({
          name: this.translate.instant('PRODUCT.EPS.PARAMETERS.' + parameter),
          type: 'spline',
          data: dataForParameter,
          tooltip: {
            valueDecimals:
              this.selectedParameter() === 'fff' ||
              this.selectedParameter() === 'power'
                ? 0
                : 1,
            valueSuffix: ' ' + unit,
          },
          color: this.getParameterColor(parameter),
          lineWidth: 2,
          marker: {
            enabled: true,
            lineColor: this.getParameterColor(parameter),
            fillColor: this.getParameterColor(parameter),
            symbol: 'circle',
            radius: 2,
          },
        });
      }
    }

    return scatterData;
  }

  getBarChartData(): Array<any> {
    const barData = [];

    for (const parameter of this.barChartParameters) {
      const dataForParameter = [];
      const unit = this.translate.instant(
        'PRODUCT.EPS.UNITS.' + this.selectedParameter()
      );

      for (const dataset of this.epsData) {
        const dtg = dataset['dtg'];
        const parameterValue = dataset?.['weatherparameters']?.[0]?.[parameter];
        if (!isNil(parameterValue)) {
          dataForParameter.push({
            x: Date.parse(dtg),
            y: parameterValue,
          });
        }
      }

      if (dataForParameter.length > 0) {
        barData.push({
          name: this.translate.instant('PRODUCT.EPS.PARAMETERS.' + parameter),
          type: 'column',
          data: dataForParameter,
          tooltip: {
            valueDecimals:
              this.selectedParameter() === 'fff' ||
              this.selectedParameter() === 'power'
                ? 0
                : 1,
            valueSuffix: ' ' + unit,
          },
          color: this.getParameterColor(parameter),
          pointPadding: -0.5,
          marker: {
            enabled: false,
          },
        });
      }
    }

    return barData;
  }*/

  /*getHighChartsLineData(): Array<any> {
    const lineData = [];

    for (const parameter of this.lineChartParameters) {
      const dataForParameter = [];
      const unit = this.translate.instant(
        'PRODUCT.EPS.UNITS.' + this.selectedParameter()
      );

      for (const dataset of this.epsData) {
        const dtg = dataset['dtg'];
        const parameterValue = dataset?.['weatherparameters']?.[0]?.[parameter];
        if (!isNil(parameterValue)) {
          dataForParameter.push({
            x: Date.parse(dtg),
            y: parameterValue,
          });
        }
      }

      if (dataForParameter.length > 0) {
        lineData.push({
          name: this.translate.instant('PRODUCT.EPS.PARAMETERS.' + parameter),
          type: 'spline',
          data: dataForParameter,
          tooltip: {
            valueDecimals:
              this.selectedParameter() === 'fff' ||
              this.selectedParameter() === 'power'
                ? 0
                : 1,
            valueSuffix: ' ' + unit,
          },
          color: this.getParameterColor(parameter),
          lineWidth: 2,
          marker: {
            enabled: false,
          },
        });
      }
    }

    return lineData;
  }

  getHighChartsRangeData(): Array<any> {
    const ranges = {
      minmax: ['min', 'max'],
      band: ['10', '90'],
    };
    const lineData = [];

    for (const range of Object.keys(ranges)) {
      const parameters = ranges[range];
      const dataForParameter = [];
      const unit = this.translate.instant(
        'PRODUCT.EPS.UNITS.' + this.selectedParameter()
      );

      for (const dataset of this.epsData) {
        const dtg = dataset['dtg'];
        const parameterZeroValue =
          dataset?.['weatherparameters']?.[0]?.[parameters[0]];
        const parameterOneValue =
          dataset?.['weatherparameters']?.[0]?.[parameters[1]];
        if (
          dataset.hasOwnProperty('weatherparameters') &&
          !isNil(parameterZeroValue) &&
          !isNil(parameterOneValue)
        ) {
          dataForParameter.push({
            x: Date.parse(dtg),
            low: parameterZeroValue,
            high: parameterOneValue,
          });
        }
      }

      if (dataForParameter.length > 0) {
        lineData.push({
          name: this.translate.instant('PRODUCT.EPS.PARAMETERS.' + range),
          type: 'arearange',
          lineWidth: 0,
          fillOpacity: 0.3,
          data: dataForParameter,
          tooltip: {
            valueDecimals:
              this.selectedParameter() === 'fff' ||
              this.selectedParameter() === 'power'
                ? 0
                : 1,
            valueSuffix: ' ' + unit,
          },
          color: this.getParameterColor(range),
          marker: {
            enabled: false,
          },
        });
      }
    }

    return lineData;
  }

  setChartData() {
    this.highChartsData.set([]);

    if (
      this.selectedParameter() === 'rrr' ||
      this.selectedParameter() === 'power'
    ) {
      const barData = this.getBarChartData();
      const scatterData = this.getScatterChartData();
      this.highChartsData.set([...barData.concat(scatterData)]);
    } else {
      const lineData = this.getHighChartsLineData();
      const rangeData = this.getHighChartsRangeData();
      this.highChartsData.set([...rangeData.concat(lineData)]);
    }
  }*/

  getHighChartsLineData(): Array<any> {
    const lineData = [];

    for (const parameter of this.lineChartParameters) {
      const dataForParameter = [];
      const unit = this.translate.instant(
        'PRODUCT.EPS.UNITS.' + this.selectedParameter()
      );

      for (const dataset of this.epsData) {
        const dtg = dataset['dtg'];
        const parameterValue = dataset?.['weatherparameters']?.[0]?.[parameter];
        if (!isNil(parameterValue)) {
          dataForParameter.push({
            x: Date.parse(dtg),
            y: parameterValue,
          });
        }
      }

      if (dataForParameter.length > 0) {
        lineData.push({
          name: this.translate.instant('PRODUCT.EPS.PARAMETERS.' + parameter),
          type: 'spline',
          data: dataForParameter,
          tooltip: {
            valueDecimals:
              this.selectedParameter() === 'fff' ||
              this.selectedParameter() === 'power'
                ? 0
                : 1,
            valueSuffix: ' ' + unit,
          },
          color: this.getParameterColor(parameter),
          lineWidth: 2.5, // Geringfügig dicker für optimalen Kontrast auf Glas
          marker: {
            enabled: false,
          },
        });
      }
    }

    return lineData;
  }

  getHighChartsRangeData(): Array<any> {
    const isDarkMode = this.isDarkMode();
    const ranges = {
      minmax: ['min', 'max'],
      band: ['10', '90'],
    };
    const lineData = [];

    for (const range of Object.keys(ranges)) {
      const parameters = ranges[range];
      const dataForParameter = [];
      const unit = this.translate.instant(
        'PRODUCT.EPS.UNITS.' + this.selectedParameter()
      );

      for (const dataset of this.epsData) {
        const dtg = dataset['dtg'];
        const parameterZeroValue =
          dataset?.['weatherparameters']?.[0]?.[parameters[0]];
        const parameterOneValue =
          dataset?.['weatherparameters']?.[0]?.[parameters[1]];
        if (
          dataset.hasOwnProperty('weatherparameters') &&
          !isNil(parameterZeroValue) &&
          !isNil(parameterOneValue)
        ) {
          dataForParameter.push({
            x: Date.parse(dtg),
            low: parameterZeroValue,
            high: parameterOneValue,
          });
        }
      }

      if (dataForParameter.length > 0) {
        lineData.push({
          name: this.translate.instant('PRODUCT.EPS.PARAMETERS.' + range),
          type: 'arearange',
          lineWidth: 0,
          // WICHTIG: Reduzierte Deckkraft im Dark Mode, damit die Bänder das Gitter nicht verschlucken
          fillOpacity: isDarkMode ? 0.12 : 0.22,
          data: dataForParameter,
          tooltip: {
            valueDecimals:
              this.selectedParameter() === 'fff' ||
              this.selectedParameter() === 'power'
                ? 0
                : 1,
            valueSuffix: ' ' + unit,
          },
          color: this.getParameterColor(range),
          zIndex: 0, // Platziert die Flächen hinter den Spline-Linien
          marker: {
            enabled: false,
          },
        });
      }
    }

    return lineData;
  }

  setChartData() {
    this.highChartsData.set([]);

    if (
      this.selectedParameter() === 'rrr' ||
      this.selectedParameter() === 'power'
    ) {
      const barData = this.getBarChartData();
      const scatterData = this.getScatterChartData();
      this.highChartsData.set([...barData.concat(scatterData)]);
    } else {
      const lineData = this.getHighChartsLineData();
      const rangeData = this.getHighChartsRangeData();
      this.highChartsData.set([...rangeData.concat(lineData)]);
    }
  }

  private buildDifferenceSeriesFromRunMap(
    runMap: Map<string, Array<{ x: number; y: number }>>,
    referenceRun: string
  ): any[] {
    const isDarkMode = this.isDarkMode();
    const unit = this.translate.instant(
      'PRODUCT.EPS.UNITS.' + this.selectedParameter()
    );

    const referencePoints = runMap.get(referenceRun) ?? [];
    const referenceMap = new Map<number, number>(
      referencePoints.map(point => [point.x, point.y])
    );

    const series: any[] = [];

    [...runMap.entries()].forEach(([run, points], index) => {
      if (run === referenceRun || !points.length) {
        return;
      }

      const diffPoints: Array<{ x: number; y: number }> = [];

      for (const point of points) {
        const refValue = referenceMap.get(point.x);
        if (refValue == null) {
          continue;
        }

        diffPoints.push({
          x: point.x,
          y: point.y - refValue,
        });
      }

      if (diffPoints.length > 0) {
        series.push({
          name: `${this.formatRunLabel(run)} - ${this.formatRunLabel(referenceRun)}`,
          type: 'spline',
          data: diffPoints,
          color: this.getComparisonSeriesColor(index),
          lineWidth: 2.2, // Leicht erhöht für scharfe Abgrenzung bei feinen Differenzen
          marker: { enabled: false },
          states: {
            hover: {
              lineWidthPlus: 1, // Hebt die Linie beim Drüberfahren hervor
            },
          },
        });
      }
    });

    return series;
  }

  private async loadRunPoints(
    run: string
  ): Promise<Array<{ x: number; y: number }>> {
    const runData = await this.epsService.getEpsData(
      this.selectedModel(),
      this.selectedStationListItem().stationId,
      this.selectedParameter(),
      run,
      true
    );

    if (!Array.isArray(runData) || runData.length === 0) {
      return [];
    }

    const points: Array<{ x: number; y: number }> = [];

    for (const dataset of runData) {
      const point = this.getComparisonPoint(dataset);
      if (point) {
        points.push(point);
      }
    }

    points.sort((a, b) => a.x - b.x);
    return points;
  }

  private async loadComparisonRunMap(): Promise<
    Map<string, Array<{ x: number; y: number }>>
  > {
    const runs = this.availableComparisonRuns();
    const runMap = new Map<string, Array<{ x: number; y: number }>>();

    for (const run of runs) {
      runMap.set(run, await this.loadRunPoints(run));
    }

    return runMap;
  }

  /*private buildDifferenceSeriesFromRunMap(
    runMap: Map<string, Array<{ x: number; y: number }>>,
    referenceRun: string
  ): any[] {
    const unit = this.translate.instant(
      'PRODUCT.EPS.UNITS.' + this.selectedParameter()
    );

    const referencePoints = runMap.get(referenceRun) ?? [];
    const referenceMap = new Map<number, number>(
      referencePoints.map(point => [point.x, point.y])
    );

    const series: any[] = [];

    [...runMap.entries()].forEach(([run, points], index) => {
      if (run === referenceRun || !points.length) {
        return;
      }

      const diffPoints: Array<{ x: number; y: number }> = [];

      for (const point of points) {
        const refValue = referenceMap.get(point.x);
        if (refValue == null) {
          continue;
        }

        diffPoints.push({
          x: point.x,
          y: point.y - refValue,
        });
      }

      if (diffPoints.length > 0) {
        series.push({
          name: `${this.formatRunLabel(run)} - ${this.formatRunLabel(referenceRun)}`,
          type: 'spline',
          data: diffPoints,
          color: this.getComparisonSeriesColor(index),
          lineWidth: 1.8,
          marker: { enabled: false },
          /!*tooltip: {
            valueDecimals: 1,
            pointFormat:
              '<span style="color:{series.color}">●</span> {series.name}: <b>{point.y:.1f}</b> ' +
              unit,
          },*!/
        });
      }
    });

    return series;
  }*/

  private stopChartLoading(): void {
    if (this.chartLoadingTimer) {
      clearTimeout(this.chartLoadingTimer);
      this.chartLoadingTimer = null;
    }
    this.loadingData = false;
  }

  private markChartAsIdle(): void {
    this.stopChartLoading();
    this.chartReady = false;
  }

  private resetToSingleMode(): void {
    this.chartMode.set('single');
  }

  async showComparisonChart(): Promise<void> {
    this.chartMode.set('comparison');

    const runs = this.availableComparisonRuns();
    if (!this.referenceRun() && runs.length > 0) {
      this.referenceRun.set(runs[0]);
    }

    await this.setComparisonChart();
  }

  async showSingleChart(): Promise<void> {
    this.chartMode.set('single');
    await this.getEpsData(false, true);
  }

  onComparisonModeChange(mode: 'ensemble' | 'difference'): void {
    this.comparisonMode.set(mode);

    const runs = this.availableComparisonRuns();
    if (mode === 'difference' && !this.referenceRun() && runs.length > 0) {
      this.referenceRun.set(runs[0]);
    }

    if (this.isComparisonMode()) {
      void this.setComparisonChart();
    }
  }

  onComparisonStatisticChange(
    statistic: (typeof this.comparisonStatistics)[number]
  ): void {
    this.comparisonStatistic.set(statistic);
    if (this.isComparisonMode()) {
      void this.setComparisonChart();
    }
  }

  onReferenceRunChange(run: string): void {
    this.referenceRun.set(run);
    if (this.isComparisonMode() && this.comparisonMode() === 'difference') {
      void this.setComparisonChart();
    }
  }

  public availableComparisonRuns(): string[] {
    return [...(this.availableModelRuns() ?? [])].sort(
      (a, b) => moment.utc(a).valueOf() - moment.utc(b).valueOf()
    );
  }

  private getComparisonValue(dataset: any): number | null {
    const weather = dataset?.weatherparameters?.[0];
    if (!weather) {
      return null;
    }

    const value = weather?.[this.comparisonStatistic()];
    return this.isNumericValue(value) ? value : null;
  }

  private getComparisonPoint(dataset: any): { x: number; y: number } | null {
    const dtg = dataset?.dtg;
    const value = this.getComparisonValue(dataset);

    if (!dtg || value == null) {
      return null;
    }

    return {
      x: moment.utc(dtg).valueOf(),
      y: value,
    };
  }

  private quantile(values: number[], q: number): number {
    if (!values.length) {
      return NaN;
    }

    const sorted = [...values].sort((a, b) => a - b);
    const pos = (sorted.length - 1) * q;
    const base = Math.floor(pos);
    const rest = pos - base;

    if (sorted[base + 1] === undefined) {
      return sorted[base];
    }

    return sorted[base] + rest * (sorted[base + 1] - sorted[base]);
  }

  private buildEnsembleSummarySeriesFromRunMap(
    runMap: Map<string, Array<{ x: number; y: number }>>
  ): any[] {
    const unit = this.translate.instant(
      'PRODUCT.EPS.UNITS.' + this.selectedParameter()
    );

    const byTimestamp = new Map<number, number[]>();

    for (const points of runMap.values()) {
      for (const point of points) {
        const values = byTimestamp.get(point.x) ?? [];
        values.push(point.y);
        byTimestamp.set(point.x, values);
      }
    }

    const timestamps = [...byTimestamp.keys()].sort((a, b) => a - b);

    const meanPoints: Array<{ x: number; y: number }> = [];
    const bandPoints: Array<{ x: number; low: number; high: number }> = [];

    for (const x of timestamps) {
      const values = byTimestamp.get(x) ?? [];
      if (!values.length) {
        continue;
      }

      const mean =
        values.reduce((sum, current) => sum + current, 0) / values.length;
      const q10 = this.quantile(values, 0.1);
      const q90 = this.quantile(values, 0.9);

      meanPoints.push({ x, y: round(mean, 2) });
      bandPoints.push({ x, low: q10, high: q90 });
    }

    const series: any[] = [];

    if (bandPoints.length > 0) {
      series.push({
        name: 'Ensemble spread (10–90%)',
        type: 'arearange',
        data: bandPoints,
        color: 'rgba(25, 118, 210, 0.18)',
        lineWidth: 0,
        fillOpacity: 0.35,
        marker: { enabled: false },
        tooltip: {
          valueSuffix: ' ' + unit,
        },
      });
    }

    if (meanPoints.length > 0) {
      series.push({
        name: 'Ensemble mean',
        type: 'spline',
        data: meanPoints,
        color: '#0d47a1',
        lineWidth: 3,
        marker: { enabled: false },
        zIndex: 5,
        tooltip: {
          valueSuffix: ' ' + unit,
        },
      });
    }

    return series;
  }

  private buildEnsembleSeriesFromRunMap(
    runMap: Map<string, Array<{ x: number; y: number }>>
  ): any[] {
    const unit = this.translate.instant(
      'PRODUCT.EPS.UNITS.' + this.selectedParameter()
    );

    const series: any[] = [];

    [...runMap.entries()].forEach(([run, points], index) => {
      if (!points.length) {
        return;
      }

      series.push({
        name: this.formatRunLabel(run),
        type: 'spline',
        data: points,
        color: this.getComparisonSeriesColor(index),
        lineWidth: 1.2,
        opacity: 0.45,
        marker: { enabled: false },
        tooltip: {
          valueSuffix: ' ' + unit,
        },
      });
    });

    return series;
  }

  public formatRunLabel(run: string): string {
    return moment.utc(run).format('DD.MM.YYYY HH:mm') + ' UTC';
  }

  /*private getComparisonSeriesColor(index: number): string {
    const comparisonColors = [
      '#1976d2',
      '#d32f2f',
      '#388e3c',
      '#6a1b9a',
      '#f57c00',
      '#00796b',
    ];
    return comparisonColors[index % comparisonColors.length];
  }*/

  private getComparisonSeriesColor(index: number): string {
    const isDarkMode = this.isDarkMode();

    // Kontraststarke Farben für beide Helligkeitsstufen
    const comparisonColors = isDarkMode
      ? [
          '#38bdf8', // Neon Sky Blue
          '#f87171', // Soft Red
          '#4ade80', // Mint Green
          '#e879f9', // Neon Orchid
          '#fb923c', // Bright Orange
          '#2dd4bf', // Bright Teal
        ]
      : [
          '#1976d2', // Deep Blue
          '#d32f2f', // Solid Red
          '#388e3c', // Grass Green
          '#6a1b9a', // Deep Purple
          '#f57c00', // Solid Orange
          '#00796b', // Dark Teal
        ];

    return comparisonColors[index % comparisonColors.length];
  }

  /*private async setComparisonChart(): Promise<void> {
    this.startChartLoading();

    try {
      const unit = this.translate.instant(
        'PRODUCT.EPS.UNITS.' + this.selectedParameter()
      );
      const stationName =
        this.selectedStationListItem()?.overwriteStationName ?? '';
      const parameterName = this.translate.instant(
        'PRODUCT.EPS.PARAMETERS.' + this.selectedParameter()
      );

      const runMap = await this.loadComparisonRunMap();

      const referenceRun =
        this.referenceRun() ?? this.availableComparisonRuns()[0];

      const overlaySeries = this.buildEnsembleSeriesFromRunMap(runMap);
      const ensembleSeries = this.buildEnsembleSummarySeriesFromRunMap(runMap);

      const series =
        this.comparisonMode() === 'difference'
          ? this.buildDifferenceSeriesFromRunMap(runMap, referenceRun)
          : [...ensembleSeries, ...overlaySeries];

      const titleSuffix =
        this.comparisonMode() === 'difference'
          ? ' (difference mode)'
          : ' (ensemble view)';

      this.chartOptions.set({
        chart: {
          zoomType: 'x',
          marginLeft: 75,
          marginRight: 90,
        },
        title: {
          text:
            this.translate.instant('PRODUCT.EPS.TITLE') +
            ' ' +
            stationName +
            ' - ' +
            parameterName +
            titleSuffix,
          align: 'left',
        },
        subtitle: {
          text:
            this.comparisonMode() === 'difference'
              ? this.translate.instant(
                  'PRODUCT.EPS.MODELRUN.DIFFERENCETOREFERENCE'
                ) + `: ${referenceRun ? this.formatRunLabel(referenceRun) : ''}`
              : this.translate.instant(
                  'PRODUCT.EPS.MODELRUN.GRAPHICDESCRIPTION'
                ), //'Thin lines = individual runs, shaded area = 10–90% spread, bold line = ensemble mean',
          align: 'left',
        },
        xAxis: {
          type: 'datetime',
          title: {
            text: 'DTG (UTC)',
          },
          labels: {
            format: '{value:%d.%m. %H:%M}',
          },
          crosshair: true,
        },
        yAxis: {
          title: {
            text:
              this.comparisonMode() === 'difference'
                ? `Difference of ${parameterName}`
                : parameterName,
          },
          min:
            this.comparisonMode() === 'difference'
              ? undefined
              : (this.fixedYAxisBounds()?.min ?? undefined),
          max:
            this.comparisonMode() === 'difference'
              ? undefined
              : (this.fixedYAxisBounds()?.max ?? undefined),
          gridLineWidth: 1,
          gridLineColor: '#808080',
          plotLines:
            this.comparisonMode() === 'difference'
              ? [
                  {
                    value: 0,
                    color: '#d32f2f',
                    width: 2,
                    zIndex: 3,
                  },
                ]
              : [],
        },
        tooltip: {
          shared: true,
          useHTML: true,
          headerFormat: '<small>{point.x:%a, %d.%m. %H:%M} UTC</small><br>',
        },
        legend: {
          enabled: true,
        },
        series,
        credits: {
          enabled: true,
          text: 'Wettermanufaktur GmbH',
          href: 'https://www.wettermanufaktur.de',
        },
      });

      this.chartReady = true;
    } finally {
      this.stopChartLoading();
    }
  }*/

  private async setComparisonChart(): Promise<void> {
    this.startChartLoading();

    try {
      const isDarkMode = this.isDarkMode();

      // Dynamische Theme-Stile für Text, Gitter und Overlays
      const textColor = isDarkMode ? '#e5e7eb' : '#2c3e50';
      const mutedTextColor = isDarkMode
        ? 'rgba(229, 231, 235, 0.6)'
        : 'rgba(44, 62, 80, 0.6)';
      const gridLineColor = isDarkMode
        ? 'rgba(255, 255, 255, 0.12)'
        : 'rgba(0, 0, 0, 0.08)';

      const unit = this.translate.instant(
        'PRODUCT.EPS.UNITS.' + this.selectedParameter()
      );
      const stationName =
        this.selectedStationListItem()?.overwriteStationName ?? '';
      const parameterName = this.translate.instant(
        'PRODUCT.EPS.PARAMETERS.' + this.selectedParameter()
      );

      const runMap = await this.loadComparisonRunMap();

      const referenceRun =
        this.referenceRun() ?? this.availableComparisonRuns()[0];

      const overlaySeries = this.buildEnsembleSeriesFromRunMap(runMap);
      const ensembleSeries = this.buildEnsembleSummarySeriesFromRunMap(runMap);

      const series =
        this.comparisonMode() === 'difference'
          ? this.buildDifferenceSeriesFromRunMap(runMap, referenceRun)
          : [...ensembleSeries, ...overlaySeries];

      const titleSuffix =
        this.comparisonMode() === 'difference'
          ? ' (difference mode)'
          : ' (ensemble view)';

      this.chartOptions.set({
        chart: {
          zoomType: 'x',
          marginLeft: 75,
          marginRight: 90,
          backgroundColor: 'transparent', // Wichtig: Aktiviert den Glassmorphism-Hintergrund deiner Card
          style: {
            fontFamily: 'Roboto, "Helvetica Neue", sans-serif',
          },
        },
        title: {
          text:
            this.translate.instant('PRODUCT.EPS.TITLE') +
            ' ' +
            stationName +
            ' - ' +
            parameterName +
            titleSuffix,
          align: 'left',
          style: { color: textColor },
        },
        subtitle: {
          text:
            this.comparisonMode() === 'difference'
              ? this.translate.instant(
                  'PRODUCT.EPS.MODELRUN.DIFFERENCETOREFERENCE'
                ) + `: ${referenceRun ? this.formatRunLabel(referenceRun) : ''}`
              : this.translate.instant(
                  'PRODUCT.EPS.MODELRUN.GRAPHICDESCRIPTION'
                ),
          align: 'left',
          style: { color: mutedTextColor },
        },
        xAxis: {
          type: 'datetime',
          title: {
            text: 'DTG (UTC)',
            style: { color: textColor },
          },
          labels: {
            format: '{value:%a %H.%M.}', // bzw. format: '{value:%d.%m. %H:%M}' im Comparison-Chart
            style: {
              color: isDarkMode ? '#f3f4f6' : '#4a5568',
              fontSize: '11px',
              fontWeight: isDarkMode ? '500' : '400',
            },
          },
          lineColor: gridLineColor,
          tickColor: gridLineColor,
          crosshair: {
            color: gridLineColor,
            dashStyle: 'Dash',
          },
        },
        yAxis: {
          title: {
            text:
              this.comparisonMode() === 'difference'
                ? `Difference of ${parameterName}`
                : parameterName,
            style: {
              color: isDarkMode ? '#ffffff' : '#2c3e50',
              fontWeight: '600',
            },
          },
          labels: {
            style: {
              color: isDarkMode ? '#f3f4f6' : '#4a5568',
              fontSize: '11px',
              fontWeight: isDarkMode ? '500' : '400',
            },
          },
          min:
            this.comparisonMode() === 'difference'
              ? undefined
              : (this.fixedYAxisBounds()?.min ?? undefined),
          max:
            this.comparisonMode() === 'difference'
              ? undefined
              : (this.fixedYAxisBounds()?.max ?? undefined),
          gridLineWidth: 1,
          gridLineColor: gridLineColor, // Flexibles Gitter statt hartem #808080
          plotLines:
            this.comparisonMode() === 'difference'
              ? [
                  {
                    value: 0,
                    color: isDarkMode ? '#f87171' : '#d32f2f', // Angepasstes Rot für den Dark Mode Kontrast
                    width: 2,
                    zIndex: 3,
                  },
                ]
              : [],
        },
        tooltip: {
          shared: true,
          useHTML: true,
          headerFormat:
            '<small style="color:' +
            mutedTextColor +
            '">{point.x:%a, %d.%m. %H:%M} UTC</small><br>',
          backgroundColor: isDarkMode
            ? 'rgba(15, 23, 42, 0.95)'
            : 'rgba(255, 255, 255, 0.95)',
          borderColor: gridLineColor,
          borderRadius: 8,
          shadow: true,
          style: { color: textColor },
        },
        legend: {
          enabled: true,
          itemStyle: { color: textColor },
          itemHoverStyle: { color: isDarkMode ? '#ffffff' : '#000000' },
          itemHiddenStyle: { color: mutedTextColor },
        },
        series,
        credits: {
          enabled: true,
          text: 'Wettermanufaktur GmbH',
          href: 'https://www.wettermanufaktur.de',
          style: { color: mutedTextColor },
        },
      });

      this.chartReady = true;
    } finally {
      this.stopChartLoading();
    }
  }
}
